import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { authActions } from '../../actions/auth';
import UROMark from './UROMark';
import {
  HomeIcon, SparkIcon, GalleryIcon, StudiosIcon, DiamondIcon,
  ChatIcon, CodeIcon, UserIcon, CaretIcon,
} from './icons';

const NAV_ITEMS = [
  { k: 'home',    l: 'Home',       Icon: HomeIcon,    path: '/explore' },
  { k: 'zen',     l: 'Zen',        Icon: SparkIcon,   path: '/zen' },
  { k: 'gallery', l: 'Gallery',    Icon: GalleryIcon, path: '/gallery' },
  { k: 'studios', l: 'Studios',    Icon: StudiosIcon, path: '/studios' },
  { k: 'brand',   l: 'Brand DNA',  Icon: DiamondIcon, path: '/brand-dna', soon: true },
  { k: 'chat',    l: 'AI Chat',    Icon: ChatIcon,    path: '/chat' },
  { k: 'api',     l: 'API & Docs', Icon: CodeIcon,    path: '/docs' },
  { k: 'account', l: 'Account',    Icon: UserIcon,    path: '/account' },
];

export default function Sidebar({ balance = 0, collapsed = false, name = 'User', email = '' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const w = collapsed ? 72 : 240;
  const dropRef = useRef(null);
  const [dropOpen, setDropOpen] = useState(false);

  const activeKey = NAV_ITEMS.find(it => location.pathname.startsWith(it.path))?.k
    || (location.pathname.startsWith('/api-usage') || location.pathname.startsWith('/sub-accounts') ? 'account' : 'home');
  const balanceTone = Number(balance) > 5 ? '#10B981' : Number(balance) > 1 ? '#F59E0B' : '#F5576C';
  const initials = name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'U';

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropOpen]);

  const handleLogout = () => {
    setDropOpen(false);
    authActions.performLogout();
    navigate('/login');
  };

  return (
    <aside className="app-shell-sidebar" style={{
      width: w, flex: `0 0 ${w}px`, height: '100%',
      background: 'rgba(255,255,255,.02)', borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column',
      padding: collapsed ? '18px 12px' : '18px 16px',
      transition: 'width .2s ease',
    }}>
      {/* Logo */}
      <div style={{ paddingLeft: collapsed ? 0 : 4, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <UROMark size={22} collapsed={collapsed} />
      </div>

      {/* Balance card */}
      {!collapsed ? (
        <div className="card" style={{ marginTop: 22, padding: 14 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase' }}>Balance</span>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: balanceTone, boxShadow: `0 0 10px ${balanceTone}` }} />
          </div>
          <div className="row" style={{ marginTop: 8, alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: balanceTone }}>{Number(balance).toFixed(4)}</span>
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>URO</span>
          </div>
          <div style={{ height: 4, background: 'rgba(255,255,255,.06)', borderRadius: 2, marginTop: 10, overflow: 'hidden' }}>
            <div style={{ width: Math.min(100, Number(balance) * 10) + '%', height: '100%', background: 'var(--grad)' }} />
          </div>
          <button onClick={() => navigate('/account')}
            style={{ display: 'inline-flex', marginTop: 10, fontSize: 12, fontWeight: 600, color: '#FFC2B5', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >Top up →</button>
        </div>
      ) : (
        <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center' }}>
          <span style={{ width: 32, height: 32, borderRadius: 8, background: balanceTone + '22', border: `1px solid ${balanceTone}44`, display: 'grid', placeItems: 'center', color: balanceTone, fontSize: 11, fontWeight: 700 }}>
            {Number(balance).toFixed(1)}
          </span>
        </div>
      )}

      {/* Nav items */}
      <nav style={{ marginTop: 24, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_ITEMS.map(it => {
          const active = it.k === activeKey;
          return (
            <button key={it.k} onClick={() => navigate(it.path)} style={{
              display: 'flex', alignItems: 'center', gap: 12, height: 36,
              padding: collapsed ? 0 : '0 10px', borderRadius: 10,
              justifyContent: collapsed ? 'center' : 'flex-start',
              background: active ? 'rgba(255,255,255,.06)' : 'transparent',
              color: active ? '#fff' : 'var(--text-3)',
              fontSize: 13, fontWeight: active ? 600 : 500,
              border: 'none', cursor: 'pointer', position: 'relative',
              width: '100%', textAlign: 'left', transition: 'background .15s, color .15s',
            }}>
              {active && (
                <span style={{ position: 'absolute', left: -16, top: 6, bottom: 6, width: 3, borderRadius: '0 3px 3px 0', background: 'var(--grad)' }} />
              )}
              <it.Icon s={16} />
              {!collapsed && (
                <span style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{it.l}</span>
                  {it.soon && (
                    <span style={{ fontSize: 9, color: '#C4B5FD', background: 'rgba(167,139,250,.12)', padding: '2px 6px', borderRadius: 999 }}>SOON</span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User block with dropdown */}
      <div ref={dropRef} style={{ position: 'relative', marginTop: 8 }}>
        {/* Dropdown */}
        {dropOpen && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0,
            background: 'rgba(12,12,20,.98)', border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 10, overflow: 'hidden', boxShadow: '0 16px 40px -8px rgba(0,0,0,.8)',
            zIndex: 100,
          }}>
            <button onClick={() => { setDropOpen(false); navigate('/account'); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#fff', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <UserIcon s={14} /> Account settings
            </button>
            <div style={{ height: 1, background: 'rgba(255,255,255,.06)' }} />
            <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#F5576C', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,87,108,.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ fontSize: 14 }}>↪</span> Log out
            </button>
          </div>
        )}

        {/* User chip */}
        <div className="row" onClick={() => setDropOpen(o => !o)} style={{
          padding: collapsed ? 0 : '10px 10px', borderRadius: 12,
          background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 10, height: collapsed ? 44 : 'auto', cursor: 'pointer',
        }}>
          <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #FF7E67, #FF4D8D)', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {initials}
          </span>
          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>{name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>
              </div>
              <CaretIcon s={12} />
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
